// Meldelisten: was lief, für GEMA, GVL und Werbekunden.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { errorText } from "../components/streaming-ui";
import { Callout, Page, PageHeader, Skeleton, StatTile } from "../components/ui";
import type { Channel } from "../types";

function lastMonths(n: number): Array<{ value: string; label: string }> {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < n; i++) {
    out.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleDateString("de-DE", { month: "long", year: "numeric" }) });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

const FORMATS = [
  { id: "gema", label: "GEMA-Meldeliste", hint: "Jeder Einsatz mit Datum, Uhrzeit, Titel, Komponist, Interpret, Verlag, ISRC, Dauer." },
  { id: "gvl", label: "GVL-Meldeliste", hint: "Jeder Einsatz mit Titel, Interpret, Label, ISRC und Spieldauer in Sekunden." },
  { id: "summary", label: "Titelübersicht", hint: "Jeder Titel einmal, mit Zahl der Einsätze und Gesamtdauer." },
  { id: "ads", label: "Werbenachweis", hint: "Jeder gesendete Spot mit Zeit und Hörern – für eure Werbekunden." },
] as const;

export function ReportsPage(): JSX.Element {
  const months = lastMonths(13);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [slug, setSlug] = useState("");
  const [month, setMonth] = useState(months[0]!.value);
  const [check, setCheck] = useState<Awaited<ReturnType<typeof api.checkPlaylog>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.listChannels().then((c) => {
      setChannels(c);
      setSlug((s) => s || c[0]?.slug || "");
    }).catch((err) => setError(errorText(err)));
  }, []);
  useEffect(() => {
    if (!slug) return;
    setCheck(null);
    api.checkPlaylog(slug, month).then(setCheck).catch((err) => setError(errorText(err)));
  }, [slug, month]);

  return (
    <Page className="max-w-6xl">
      <PageHeader
        eyebrow="Auswerten"
        title="Meldelisten"
        description={
          <>
            Die Suite protokolliert jeden gesendeten Titel – auch Live-Sendungen, sofern die Moderationssoftware Titel mitschickt.
            Die Listen sind CSV-Dateien für Excel oder LibreOffice. <b className="text-secondary">Vor dem ersten Einreichen</b> bitte mit GEMA bzw. GVL abstimmen,
            welche Spalten und welches Format sie für euren Vertrag erwarten; die Listen enthalten die üblichen Angaben.
          </>
        }
        actions={
          <>
            {channels.length > 1 && (
              <select value={slug} onChange={(e) => setSlug(e.target.value)} className="input !w-auto">
                {channels.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
              </select>
            )}
            <select value={month} onChange={(e) => setMonth(e.target.value)} className="input !w-auto font-medium">
              {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </>
        }
      />
      <div className="space-y-5">
      {error && <Callout tone="danger">{error}</Callout>}

      {!check && !error && <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>}
      {check && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatTile label="Musik-Einsätze" value={check.music.toLocaleString("de-DE")} tone="brand" />
            <StatTile label="Live-Titel" value={check.live.toLocaleString("de-DE")} />
            <StatTile label="Werbespots" value={check.ads.toLocaleString("de-DE")} />
            <StatTile
              label="Ohne Komponist / ISRC"
              value={`${check.no_composer} / ${check.no_isrc}`}
              tone={check.music === 0 ? "neutral" : check.no_composer > 0 || check.no_isrc > 0 ? "warning" : "success"}
              hint="Einsätze mit fehlender Angabe"
            />
          </div>
          <p className="sr-only">
            {check.music.toLocaleString("de-DE")} Musik-Einsätze, {check.live.toLocaleString("de-DE")} Live-Titel, {check.ads.toLocaleString("de-DE")} Werbespots.
          </p>
          {check.music > 0 && (check.no_composer > 0 || check.no_isrc > 0) ? (
            <div className="callout callout-warning block">
              Bei <b>{check.no_composer}</b> Einsätzen fehlt der Komponist, bei <b>{check.no_isrc}</b> die ISRC. Ergänzen lässt sich das in der
              <Link to="/library" className="mx-1 font-medium underline">Mediathek</Link>unter „Angaben“; Nachträge gelten auch für schon gesendete Einsätze. Am häufigsten betroffen:
              <ul className="mt-2 grid list-disc gap-x-6 pl-5 text-xs sm:grid-cols-2">
                {check.missing_details.slice(0, 10).map((m) => <li key={m.id}>{[m.artist, m.title].filter(Boolean).join(" – ")} <span className="tabular-nums opacity-75">({m.plays}×)</span></li>)}
              </ul>
            </div>
          ) : check.music > 0 ? (
            <Callout tone="success">✓ Komponist und ISRC sind bei allen Einsätzen vorhanden.</Callout>
          ) : null}
        </>
      )}

      <div>
        <h2 className="section-label mb-3">Listen herunterladen</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FORMATS.map((f) => (
            <div key={f.id} className="card flex flex-col justify-between gap-4 p-5">
              <div className="flex gap-3">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-brand-primary/10 text-[0.65rem] font-bold tracking-wide text-brand-primary">CSV</span>
                <div className="min-w-0">
                  <div className="font-semibold text-primary">{f.label}</div>
                  <div className="mt-0.5 text-xs leading-relaxed text-muted">{f.hint}</div>
                </div>
              </div>
              <button onClick={() => void api.downloadPlaylog(slug, month, f.id).catch((err) => setError(errorText(err)))}
                disabled={!slug} className="btn btn-secondary self-start">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true"><path d="M12 4v11m0 0-4-4m4 4 4-4M5 20h14" /></svg>
                CSV herunterladen
              </button>
            </div>
          ))}
        </div>
      </div>
      </div>
    </Page>
  );
}
